<template>
  <tr>
    <td class="td-compact">
      <black-white-list-enable-button
        :model-value="vModelEnabled"
        @update:model-value="(enabled) => updateEntityField('enabled', enabled)"
      />
    </td>
    <td class="td-compact">
      <black-white-list-type-select
        :model-value="vModelType"
        @update:model-value="(type) => updateEntityField('type', type)"
      />
    </td>
    <td class="td-compact">
      <participants-select
        v-model:text="string"
        :model-value="(vModelPhones as (Contact[keyof Contact] | string)[])"
        :items="participantsItems"
        :all-items="allItems"
        :id-key="['internalNumber', 'mobilePhone']"
        :fields-search-by="fieldsSearchBy"
        :on-end-reached-cb="fetchNext"
        multiple
        is-table
        server-side-filter
        cant-be-empty
        @update:model-value="updatePhones"
      >
        <template #suggestion="{ suggestion }">
          <participant-item
            :contact="suggestion.value"
          />
        </template>
      </participants-select>
    </td>
    <td class="td-compact">
      <black-white-list-schedule-button
        :model-value="vModelSchedule!"
        class="px-2"
        @update:model-value="updateEntityField('schedule', $event)"
        @remove-schedule="updateEntityField('schedule', undefined)"
      />
    </td>
    <td class="td-compact">
      <div class="flex">
        <black-white-list-create-button
          :item="props.item"
          :disabled="invalid"
          :is-new="isDraft!"
          @saved="emit('saved')"
        />
        <black-white-list-delete-button
          v-if="!isDraft"
          :guid="props.item.guid"
        />
      </div>
    </td>
  </tr>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import {
  BlackWhiteListEnableButton,
  BlackWhiteListTypeSelect,
  BlackWhiteListScheduleButton,
  BlackWhiteListCreateButton,
  BlackWhiteListDeleteButton,
} from '@/features/black-white-list'

import { useBlackWhiteListStore } from '@/entities/black-white-list'
import { ParticipantItem, useContactParticipantsSelectSearch } from '@/entities/contact'

import { draftPhonesToAddToBackend, ParticipantsSelect } from '@/shared/components/participants-select'
import { Contact } from '@/shared/types/api'

import { BlackWhiteListEntity, BlackWhiteListEntitySchedule, BlackWhiteListType } from '../types'

/** Значения, которые эмитит participants-select при :items="Contact[]" (T[K] | string). */
type ParticipantsSelectModelElement = Contact[keyof Contact] | Contact | string

const isContactOrString = (item: ParticipantsSelectModelElement): item is Contact | string =>
  typeof item === 'string'
  || (typeof item === 'object' && item !== null && ('internalNumber' in item || 'mobilePhone' in item))

const { string, fetchNext, participantsItems, allItems, fieldsSearchBy } = useContactParticipantsSelectSearch()
const store = useBlackWhiteListStore()
const { entities, draft } = storeToRefs(store)

const props = withDefaults(defineProps<{
  item?: BlackWhiteListEntity
  isDraft?: boolean
}>(), {
  item: () => ({
    guid: '',
    enabled: false,
    type: 'black',
  }),
  isDraft: false,
})

const emit = defineEmits<{
  (event: 'saved'): void
}>()

const draftNumbersToAdd = ref<ParticipantsSelectModelElement[]>([])

const innerValue = ref({ ...props.item, schedule: { ...props.item.schedule }})

const invalid = computed(() => {
  return !innerValue.value || !innerValue.value.phones?.length && !props.isDraft
})

const vModelEnabled = computed(() => {
  return props.isDraft ? draft.value.enabled : innerValue.value.enabled
})

const vModelType = computed(() => {
  return props.isDraft ? draft.value.type : innerValue.value.type
})

const vModelPhones = computed(() => {
  return props.isDraft ? draftNumbersToAdd.value : innerValue.value.phones?.split(';') ?? []
})

const vModelSchedule = computed(() => {
  return props.isDraft ? draft.value.schedule : innerValue.value.schedule
})

const updatePhones = (phonesToUpdate: ParticipantsSelectModelElement[]) => {
  const filtered = phonesToUpdate.filter(isContactOrString)
  if (props.isDraft) {
    draftNumbersToAdd.value = [...filtered]
    draft.value.phones = draftPhonesToAddToBackend(filtered)
  } else {
    updateEntityPhones(props.item.guid, filtered)
  }
}

const updateEntityField = (field: 'enabled' | 'type' | 'schedule', value: boolean | BlackWhiteListType | BlackWhiteListEntitySchedule | undefined) => {
  if (props.isDraft) {
    draft.value[field] = value as never
  }
  const entity = entities.value.find((e) => e.guid === props.item.guid)
  if (entity) {
    entity[field] = value as never
  }
}

const updateEntityPhones = (guid: string, phonesArray: (Contact | string)[]) => {
  const entity = entities.value.find((e) => e.guid === guid)
  if (entity) {
    entity.phones = draftPhonesToAddToBackend(phonesArray)
  }
}

watch(
  () => props.item,
  () => {
    innerValue.value = { ...props.item, schedule: { ...props.item.schedule }}
  },
  { deep: true },
)
</script>
